import { FilledInputProps, IconButton, InputProps, OutlinedInputProps, TextField } from '@mui/material';
import { FC, useState } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import FormField from '@acorn-portal/shared/src/components/inputs/FormField';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import CurrencyFormat from './formats/CurrencyInputFormat';
import PhoneFormat from './formats/PhoneInputFormat';
import AccountNumberFormat from './formats/AccountNumberInputFormat';
import SortCodeFormat from './formats/SortCodeFormat';

interface IModal {
	title: string;
	description: string;
}

interface IProps {
	onChange: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
	name?: string;
	id?: string;
	error?: boolean;
	helperText?: string | false;
	value?: unknown;
	label: string;
	format?: 'currency' | 'phone' | 'accountNumber' | 'sortCode' | 'password' | 'email';
	modal?: IModal;
	onBlur?: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>;
	autoComplete?:
		| 'given-name'
		| 'family-name'
		| 'address-line1'
		| 'address-line2'
		| 'address-line3'
		| 'address-level2'
		| 'address-level1'
		| 'postal-code'
		| 'email'
		| 'tel'
		| 'on';
	InputProps?: Partial<OutlinedInputProps> | Partial<FilledInputProps> | Partial<InputProps>;
}

const FormTextField: FC<IProps> = ({
	error,
	helperText,
	id,
	label,
	modal,
	format,
	onBlur,
	onChange,
	autoComplete,
	InputProps,
	...rest
}) => {
	const [open, toggleModal] = useState(false);

	const getFormat = () => {
		switch (format) {
			case 'currency':
				return CurrencyFormat;
			case 'phone':
				return PhoneFormat;
			case 'accountNumber':
				return AccountNumberFormat;
			case 'sortCode':
				return SortCodeFormat;
			default:
				return undefined;
		}
	};

	const getInputMode = () => {
		switch (format) {
			case 'currency':
				return 'decimal';
			case 'accountNumber':
			case 'sortCode':
				return 'numeric';
			case 'phone':
				return 'tel';
			case 'email':
				return 'email';
			default:
				return 'text';
		}
	};

	return (
		<>
			<FormField error={error} helperText={helperText} id={id} label={label}>
				<TextField
					id={id}
					autoComplete={autoComplete}
					type={format === 'password' ? 'password' : undefined}
					onChange={(event) => {
						onChange(event);
					}}
					onBlur={(event) => {
						if (onBlur) onBlur(event);
					}}
					sx={{
						width: modal ? '92%' : '100%',
						'& fieldset': {
							borderColor: error ? 'error.main' : 'unset',
						},
					}}
					{...rest}
					slotProps={{
						input: {
							inputMode: getInputMode(),
							inputComponent: getFormat() as any,
							...InputProps,
						},
					}}
				/>
				{modal && (
					<IconButton
						color='icon'
						aria-label={`${id ?? ''}-more-info`}
						component='span'
						sx={{ my: 1, width: '8%' }}
						onClick={() => toggleModal(true)}
					>
						<InfoIcon />
					</IconButton>
				)}
			</FormField>
			{modal && <HelperModal onClose={() => toggleModal(false)} open={open} {...modal} />}
		</>
	);
};

export default FormTextField;
